import React, { useState, useEffect } from 'react'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import useAddExternalUserpageStyles from './styles'
import {
  AddExternalUserNew,
  UpdateUserNew,
  FetchPlantListNew,
  FetchRoles,
  FetchOrganizations
} from '../../../../services/api'
import CustomAlert from '../../../../components/CustomAlert/CustomAlert'
import PlantSelection from './utils/PlantSelection'
import dayjs from 'dayjs';

const AddExternalUserspage = ({ handleTabChange, handleUserlistTabChange, logic, isModal, onClose }) => {
  const classes = useAddExternalUserpageStyles()
  const theme = useTheme()
  const [plantList, setPlantList] = React.useState([])
  const [rolesList, setRolesList] = useState([])
  const [orgList, setOrgList] = useState([])
  const [isEditMode, setIsEditMode] = useState(false)

  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setalertMessage] = useState('')
  const [alertSeverity, setAlertSeverity] = useState('success')

  const token = localStorage.getItem('accessToken')

  const ITEM_HEIGHT = 48
  const ITEM_PADDING_TOP = 8
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  }

  const validationSchema = Yup.object({
    firstName: Yup.string().trim().min(2, 'First Name must be at least 2 characters').required('First Name is required'),
    lastName: Yup.string().trim().min(2, 'Last Name must be at least 2 characters').required('Last Name is required'),
    email: Yup.string().email('Invalid email format').required('Email ID is required'),
    phone: Yup.string().matches(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number').required('Contact Number is required'),
    userType: Yup.string().required('User type is required'),
    organizationId: Yup.string().required('Organization is required'),
    roleId: Yup.string().required('Role is required'),
    plantIds: Yup.array().min(1, 'Choose at least one plant').required('Choose at least one plant'),
    password: Yup.string()
      .when('isEditMode', {
        is: false,
        then: () => Yup.string()
          .min(8, 'Password must be at least 8 characters')
          .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 'Password must contain 1 uppercase, 1 lowercase, 1 number, and 1 special char')
          .required('Password is required'),
        otherwise: () => Yup.string()
          .min(8, 'Password must be at least 8 characters')
          .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 'Password must contain 1 uppercase, 1 lowercase, 1 number, and 1 special char')
          .nullable().optional()
      }),
    confirmPassword: Yup.string()
      .when('password', {
        is: (val) => val && val.length > 0,
        then: () => Yup.string().oneOf([Yup.ref('password')], 'Passwords must match').required('Confirm Password is required'),
        otherwise: () => Yup.string().nullable().optional()
      })
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      userType: '',
      organizationId: '',
      roleId: '',
      plantIds: [],
      password: '',
      confirmPassword: '',
      isEditMode: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      let selectedEntityId = null;
      // Logic to pick entity from selected plant
      if (values.plantIds.length > 0) {
        const selectedPlantId = values.plantIds[0];
        const selectedPlant = plantList.find(
          (p) =>
            p.id === selectedPlantId ||
            p._id === selectedPlantId ||
            p._id?.$oid === selectedPlantId
        );

        if (selectedPlant) {
          selectedEntityId = selectedPlant.entityId?.$oid || selectedPlant.entityId;
        }
      }

      const payload = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        userType: 'external',
        status: 'active',
        organizationId: values.organizationId,
        entityId: selectedEntityId,
        assignedPlantIds: values.plantIds,
        roleIds: [values.roleId],
        metadata: {
          externalUserType: values.userType,
        },
      };

      if (values.password) {
        payload.password = values.password;
      }

      try {
        if (isEditMode && logic?.editData) {
          const userId = logic.editData.id || logic.editData._id || logic.editData.userId;
          const resp = await UpdateUserNew(userId, payload, token);
          if (resp?.data) {
            logic.setters.setEditData(null);
            setAlertOpen(true);
            setalertMessage(`User updated successfully`);
            setAlertSeverity('success');
            setTimeout(() => {
              if (onClose) onClose();
              else {
                handleTabChange('1', '1');
                handleUserlistTabChange('2', '2');
              }
            }, 3000);
          }
        } else {
          const resp = await AddExternalUserNew(payload, token);
          if (resp?.data && (resp?.status === 201 || resp?.status === 200)) {
            setAlertOpen(true);
            setalertMessage(`User created successfully`);
            setAlertSeverity('success');
            setTimeout(() => {
              if (onClose) onClose();
              else {
                handleTabChange('1', '1');
                handleUserlistTabChange('2', '2');
              }
            }, 3000);
          } else if (resp?.status === 400 || resp?.data?.error?.name === 'BadRequestError') {
            setAlertOpen(true);
            setalertMessage(resp?.data?.error?.message || 'User with this email already exists.');
            setAlertSeverity('error');
          } else {
            setAlertOpen(true);
            setalertMessage('An unexpected error occurred. Please try again.');
            setAlertSeverity('error');
          }
        }
      } catch (error) {
        console.error(error);
        setAlertOpen(true);
        setalertMessage(error.response?.data?.message || 'An error occurred');
        setAlertSeverity('error');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleChangePlantName = (event) => {
    const { target: { value } } = event;
    const val = typeof value === 'string' ? value.split(',') : value;
    formik.setFieldValue('plantIds', val);
  }

  useEffect(() => {
    const fetchPlantList = async () => {
      try {
        const resp = await FetchPlantListNew(
          dayjs().format('YYYY-MM-DD'),
          token,
          '1'
        )
        if (resp?.data?.data) {
          setPlantList(resp.data.data)
        } else {
          setPlantList([])
        }
      } catch (error) {
        console.error('Error while fetching plant list:', error)
      }
    }

    fetchPlantList()

    const fetchMasterData = async () => {
      try {
        const rolesResp = await FetchRoles(token)
        if (rolesResp?.data?.roles) setRolesList(rolesResp.data.roles)

        const orgResp = await FetchOrganizations(token)
        if (orgResp?.data?.data) setOrgList(orgResp.data.data)
      } catch (err) {
        console.error('Failed to fetch master data', err)
      }
    }
    fetchMasterData()
  }, [token]);

  // Pre-fill data if in Edit Mode
  useEffect(() => {
    if (logic?.editData) {
      const data = logic.editData;
      setIsEditMode(true);
      const plantIdsFromData = data.plants?.map(p => p.id || p._id) || data.metadata?.plantIds || [];

      // Enrich plantList with user's assigned plants if they have names but are missing from main list
      if (data.plants && Array.isArray(data.plants)) {
        setPlantList(prev => {
          const existingIds = new Set(prev.map(p => p.id || p._id));
          const missing = data.plants
            .filter(p => !existingIds.has(p.id))
            .map(p => ({ ...p, id: p.id, plantName: p.name }));

          if (missing.length > 0) {
            return [...prev, ...missing];
          }
          return prev;
        });
      }

      formik.setValues({
        firstName: data.firstName || data.name?.split(' ')[0] || "",
        lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || "",
        email: data.email || "",
        phone: data.phone || "",
        userType: data.metadata?.externalUserType || "Client",
        organizationId: data.organization?.id || data.organizationId || data.metadata?.organizationId || "",
        roleId: data.role?.id || data.roles?.[0]?.id || data.metadata?.roleId || "",
        plantIds: plantIdsFromData,
        password: '',
        confirmPassword: '',
        isEditMode: true
      });
    } else {
      setIsEditMode(false);
      formik.setFieldValue('isEditMode', false);
    }
  }, [logic?.editData]);

  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setAlertOpen(false);
  }

  return (
    <div className={classes.root}>
      <CustomAlert
        open={alertOpen}
        handleClose={handleAlertClose}
        message={alertMessage}
        severity={alertSeverity}
      />
      <form onSubmit={formik.handleSubmit}>
        <div className="graph_bg">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <PlantSelection
                personName={formik.values.plantIds}
                handleChangePlantName={handleChangePlantName}
                plantList={plantList}
                errors={{ personName: formik.touched.plantIds && formik.errors.plantIds }}
                theme={theme}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={formik.touched.userType && Boolean(formik.errors.userType)}>
                <InputLabel id="user-type-label">User Type</InputLabel>
                <Select
                  labelId="user-type-label"
                  id="userType"
                  name="userType"
                  value={formik.values.userType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="User Type"
                >
                  <MenuItem value="Client">Client</MenuItem>
                  <MenuItem value="Investor">Investor</MenuItem>
                  <MenuItem value="Vendor">Vendor</MenuItem>
                </Select>
                {formik.touched.userType && formik.errors.userType && (
                  <p style={{ color: '#d32f2f', fontSize: '0.75rem', marginLeft: '14px', marginTop: '3px' }}>
                    {formik.errors.userType}
                  </p>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={formik.touched.organizationId && Boolean(formik.errors.organizationId)}>
                <InputLabel id="user-org-label">Organization</InputLabel>
                <Select
                  labelId="user-org-label"
                  id="organizationId"
                  name="organizationId"
                  value={formik.values.organizationId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Organization"
                >
                  {orgList.map((org) => (
                    <MenuItem key={org.id} value={org.id}>{org.organizationName}</MenuItem>
                  ))}
                </Select>
                {formik.touched.organizationId && formik.errors.organizationId && (
                  <p style={{ color: '#d32f2f', fontSize: '0.75rem', marginLeft: '14px', marginTop: '3px' }}>
                    {formik.errors.organizationId}
                  </p>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={formik.touched.roleId && Boolean(formik.errors.roleId)}>
                <InputLabel id="user-role-label">Role</InputLabel>
                <Select
                  labelId="user-role-label"
                  id="roleId"
                  name="roleId"
                  value={formik.values.roleId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Role"
                >
                  {rolesList.map((role) => (
                    <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                  ))}
                </Select>
                {formik.touched.roleId && formik.errors.roleId && (
                  <p style={{ color: '#d32f2f', fontSize: '0.75rem', marginLeft: '14px', marginTop: '3px' }}>
                    {formik.errors.roleId}
                  </p>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  variant="outlined"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  variant="outlined"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="phone"
                  name="phone"
                  label="Contact No."
                  variant="outlined"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="email"
                  name="email"
                  label="Email ID"
                  variant="outlined"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </FormControl>
            </Grid>
            {(!isEditMode || formik.values.password) && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      id="password"
                      name="password"
                      label={isEditMode ? "New Password (Optional)" : "Password"}
                      variant="outlined"
                      type="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Confirm Password"
                      variant="outlined"
                      type="password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                      helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                    />
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
          <button
            type="submit"
            className={`save_btn ${formik.isValid && (formik.dirty || isEditMode) ? "" : "disabled"}`}
            disabled={formik.isSubmitting || !(formik.isValid && (formik.dirty || isEditMode))}
          >
            {formik.isSubmitting ? "Saving..." : (isEditMode ? 'Update' : 'Save')}
          </button>
        </div>
      </form>
    </div>
  )
}

AddExternalUserspage.propTypes = {}

export default AddExternalUserspage
